import { urlFor } from "@/lib/sanity";

type Props = {
  image: any;
  width: number;
  height?: number;
  alt: string;
  sizes?: string;
  className?: string;
  loading?: "eager" | "lazy";
  decoding?: "async" | "sync" | "auto";
  fit?: "crop" | "clip" | "fill" | "fillmax" | "max" | "min" | "scale";
  fetchPriority?: "high" | "low" | "auto";
  srcSetWidths?: number[];
};

/**
 * Renders a Sanity image with responsive srcSet.
 * When `height` is provided, applies fit (default "crop") and generates
 * proportional heights for each srcSet width.
 */
export function SanityImage({
  image,
  width,
  height,
  alt,
  sizes,
  className,
  loading = "lazy",
  decoding = "async",
  fit = "crop",
  fetchPriority,
  srcSetWidths,
}: Props) {
  if (!image) return null;

  const build = (w: number) => {
    let b = urlFor(image).width(w).auto("format");
    if (height !== undefined) {
      const h = Math.round((height / width) * w);
      b = b.height(h).fit(fit);
    }
    return b.url();
  };

  const widths = srcSetWidths ?? [
    Math.round(width / 2),
    width,
    width * 2 > 1600 ? width * 2 : Math.round(width * 1.5),
  ];
  const srcSet = widths.map((w) => `${build(w)} ${w}w`).join(", ");

  return (
    <img
      src={build(width)}
      srcSet={srcSet}
      sizes={sizes}
      alt={alt}
      className={className}
      loading={loading}
      decoding={decoding}
      fetchPriority={fetchPriority}
    />
  );
}